/**
 * Document Persistence Service
 * 
 * Unified service for persisting imported documents/files to Supabase.
 * This service ensures that ALL imported files (from Google Drive, local uploads, 
 * Notion, GitHub, YouTube, etc.) are properly stored in the database with
 * appropriate linkage to threads, spaces, and topics.
 * 
 * CRITICAL: This service replaces any pattern where files were only kept in
 * React state (fileAttached) or local storage. Everything must persist to Supabase.
 * 
 * Storage Strategy:
 * 1. Binary files -> Supabase Storage (bucket: 'files')
 * 2. Source metadata -> 'sources' table (with linkedThreads, linkedSpaceIds arrays)
 */

import { supabase, getCurrentSession } from "@/lib/supabase";
import { extractTextFromBlob } from "@/utils/textExtractionHelper";
import { sourcesService } from "./sourcesService";
import type { Source } from "@shared/schema";

export interface PersistenceContext {
  threadId?: string | null;
  spaceId?: string | null;
  topicId?: string | null;
  folderId?: string | null;
  /** When set, created source belongs to this workspace; when null/undefined, personal. */
  workspaceId?: string | null;
}

export interface FileUploadResult {
  storageId: string;
  filename: string;
  url: string;
}

export interface PersistedDocument {
  sourceId: string;
  storageId?: string;
  title: string;
  content: string;
  type: string;
  url?: string;
  filename?: string;
}

export interface PersistDocumentOptions {
  title: string;
  content: string;
  type: 'document' | 'pdf' | 'url' | 'youtube' | 'notion' | 'github' | 'google-drive';
  url?: string;
  filename?: string;
  storageId?: string;
  storageUrl?: string;
  googleDriveFileId?: string;
  googleDriveFolderId?: string;
  folderPath?: string;
  context: PersistenceContext;
  extractedText?: string;
  mimeType?: string;
}

class DocumentPersistenceService {
  private async getCurrentUserId(): Promise<string> {
    const session = await getCurrentSession();
    if (!session?.user) {
      throw new Error("User not authenticated");
    }
    return session.user.id;
  }

  /**
   * Upload a file to Supabase Storage and create a Source entry
   * This is the main method for persisting imported files
   */
  async uploadAndPersist(
    file: File | Blob,
    filename: string,
    mimeType: string,
    context: PersistenceContext,
    options?: {
      googleDriveFileId?: string;
      folderPath?: string;
      extractedText?: string;
    }
  ): Promise<PersistedDocument> {
    const userId = await this.getCurrentUserId();

    const uploadResult = await sourcesService.uploadFile(file, filename, mimeType);

    let content = options?.extractedText || "";
    if (!content && mimeType.startsWith("text/")) {
      try {
        content = await file.text();
      } catch {
        content = `[Binary file: ${filename}]`;
      }
    }

    if (!content.trim()) {
      try {
        const extracted = await extractTextFromBlob(file, filename, mimeType);
        if (extracted.text?.trim()) {
          content = extracted.text;
        }
      } catch {
        // keep empty; fallback placeholder below
      }
    }

    const sourceType = this.inferSourceType(mimeType, filename);
    
    const linkedThreads = context.threadId ? [context.threadId] : [];
    const linkedSpaceIds = context.spaceId ? [context.spaceId] : [];

    const sourceData = {
      userId,
      title: filename,
      content: content || `Imported file: ${filename}`,
      type: sourceType,
      url: uploadResult.url,
      topics: [],
      subTopics: [],
      tags: options?.folderPath ? [options.folderPath] : [],
      linkedThreads,
      linkedSpaceIds,
      googleDriveFileId: options?.googleDriveFileId || null,
      folderId: context.folderId || null,
      attachments: [{
        id: uploadResult.id,
        filename: uploadResult.filename,
        url: uploadResult.url,
      }],
    };

    const source = await sourcesService.createSource(sourceData);

    return {
      sourceId: source.id,
      storageId: uploadResult.id,
      title: filename,
      content: content || "",
      type: sourceType,
      url: uploadResult.url,
      filename,
    };
  }

  /**
   * Persist a document without file upload (e.g., URL, YouTube transcript, Notion page)
   */
  async persistDocument(options: PersistDocumentOptions): Promise<PersistedDocument> {
    const userId = await this.getCurrentUserId();

    const linkedThreads = options.context.threadId ? [options.context.threadId] : [];
    const linkedSpaceIds = options.context.spaceId ? [options.context.spaceId] : [];

    const sourceData = {
      userId,
      title: options.title,
      content: options.content,
      type: options.type,
      url: options.url || options.storageUrl || null,
      topics: [],
      subTopics: [],
      tags: options.folderPath ? [options.folderPath] : [],
      linkedThreads,
      linkedSpaceIds,
      googleDriveFileId: options.googleDriveFileId || null,
      googleDriveFolderId: options.googleDriveFolderId || null,
      folderId: options.context.folderId || null,
      attachments: options.storageId ? [{
        id: options.storageId,
        filename: options.filename || options.title,
        url: options.storageUrl || "",
      }] : [],
    };

    const source = await sourcesService.createSource(sourceData, options.context.workspaceId ?? null);

    return {
      sourceId: source.id,
      storageId: options.storageId,
      title: options.title,
      content: options.content,
      type: options.type,
      url: options.url || options.storageUrl,
      filename: options.filename,
    };
  }

  /**
   * Link an existing source to a thread (when user attaches already-imported source)
   */
  async linkSourceToThread(sourceId: string, threadId: string): Promise<void> {
    const source = await sourcesService.getSourceById(sourceId);
    if (!source) {
      throw new Error(`Source not found: ${sourceId}`);
    }

    const currentLinkedThreads = source.linkedThreads || [];
    if (!currentLinkedThreads.includes(threadId)) {
      await sourcesService.updateSource(sourceId, {
        linkedThreads: [...currentLinkedThreads, threadId],
      });
    }
  }

  /**
   * Link an existing source to a space
   */
  async linkSourceToSpace(sourceId: string, spaceId: string): Promise<void> {
    const source = await sourcesService.getSourceById(sourceId);
    if (!source) {
      throw new Error(`Source not found: ${sourceId}`);
    }

    const currentLinkedSpaces = (source as any).linkedSpaceIds || [];
    if (!currentLinkedSpaces.includes(spaceId)) {
      await sourcesService.updateSource(sourceId, {
        linkedSpaceIds: [...currentLinkedSpaces, spaceId],
      } as any);
    }
  }

  /**
   * Get all sources linked to a specific thread
   */
  async getSourcesForThread(threadId: string): Promise<Source[]> {
    return sourcesService.getSourcesByThreadId(threadId);
  }

  /**
   * Get all sources linked to a specific space
   */
  async getSourcesForSpace(spaceId: string): Promise<Source[]> {
    return sourcesService.getSources(spaceId);
  }

  /**
   * Bulk persist multiple files (e.g., from Google Drive folder import)
   */
  async bulkUploadAndPersist(
    files: Array<{
      file: File | Blob;
      filename: string;
      mimeType: string;
      googleDriveFileId?: string;
      folderPath?: string;
      extractedText?: string;
    }>,
    context: PersistenceContext,
    onProgress?: (completed: number, total: number, current: string) => void
  ): Promise<PersistedDocument[]> {
    const results: PersistedDocument[] = [];
    const total = files.length;

    for (let i = 0; i < files.length; i++) {
      const fileData = files[i];
      onProgress?.(i, total, fileData.filename);

      try {
        const result = await this.uploadAndPersist(
          fileData.file,
          fileData.filename,
          fileData.mimeType,
          context,
          {
            googleDriveFileId: fileData.googleDriveFileId,
            folderPath: fileData.folderPath,
            extractedText: fileData.extractedText,
          }
        );
        results.push(result);
      } catch (error) {
        console.error(`Failed to persist ${fileData.filename}:`, error);
      }
    }

    onProgress?.(total, total, "Complete");
    return results;
  }

  private inferSourceType(mimeType: string, filename: string): string {
    if (mimeType === "application/pdf") return "pdf";
    if (mimeType.includes("word") || mimeType.includes("document")) return "document";
    if (mimeType.includes("spreadsheet") || mimeType.includes("excel")) return "spreadsheet";
    if (mimeType.includes("presentation") || mimeType.includes("powerpoint")) return "presentation";
    if (mimeType.startsWith("text/")) return "text";
    if (mimeType.startsWith("image/")) return "image";
    if (mimeType.startsWith("audio/")) return "audio";
    if (mimeType.startsWith("video/")) return "video";
    
    const ext = filename.toLowerCase().split('.').pop() || '';
    const typeMap: Record<string, string> = {
      pdf: "pdf",
      doc: "document",
      docx: "document",
      txt: "text",
      md: "markdown",
      json: "json",
      csv: "spreadsheet",
      xls: "spreadsheet",
      xlsx: "spreadsheet",
      ppt: "presentation",
      pptx: "presentation",
    };
    
    return typeMap[ext] || "document";
  }
}

export const documentPersistenceService = new DocumentPersistenceService();
